// Free gift auto-add (docs/free-gift-auto-add.md).
// On templates with the "Free gift (auto-add)" section, adds the $0 gift when a trigger product is in the cart.
// On every page, removes a gift whose trigger has left the cart. A gift the shopper removes stays removed this session.
(() => {
  if (window.IRFreeGift) return;

  const DECLINED_KEY = 'irFreeGift.declined';
  const MAX_MUTATIONS = 3;
  const script = document.currentScript;
  const isGift = (item) => !!item && item.product_type === 'Free Gift';
  const giftFor = (item) => String((item.properties && item.properties._ir_gift_for) || '').split(',').map(Number).filter(Boolean);
  // Trigger ids are product ids (any variant counts) or variant ids (only that option counts).
  const matches = (item, ids) => ids.includes(Number(item.product_id)) || ids.includes(Number(item.variant_id));
  // A gift added with subscriptionsOnly carries _ir_gift_sub: only a Subscribe & Save line (one with a selling plan)
  // of its trigger keeps it (INT-515, the inositol ad page's subscribers-only mystery gift).
  const isSubscription = (item) => !!(item && item.selling_plan_allocation);
  const needsSubscription = (gift) => !!(gift.properties && gift.properties._ir_gift_sub);
  const keepsGift = (gift) => (item) => matches(item, giftFor(gift)) && (!needsSubscription(gift) || isSubscription(item));
  const isFullCart = (data) => !!data && Array.isArray(data.items) && typeof data.item_count === 'number';

  const readConfig = () => {
    const el = document.querySelector('script[type="application/json"][data-ir-free-gift-config]');
    if (!el) return null;
    try {
      const raw = JSON.parse(el.textContent);
      const giftVariantId = Number(raw.giftVariantId);
      const triggerIds = (Array.isArray(raw.triggerIds) ? raw.triggerIds : []).map(Number).filter(Boolean);
      if (!giftVariantId || !triggerIds.length) return null;
      return raw.subscriptionsOnly === true ? { giftVariantId, triggerIds, subscriptionsOnly: true } : { giftVariantId, triggerIds };
    } catch (err) {
      return null;
    }
  };
  const config = readConfig();

  let giftKnownInCart = !!(script && script.dataset && script.dataset.giftInCart === 'true');
  let lastCart = null;
  let lastRemovedKey = null;
  let declinedThisPage = false;
  let busy = false;
  let rerun = false;

  const isDeclined = () => {
    if (declinedThisPage) return true;
    try { return sessionStorage.getItem(DECLINED_KEY) === '1'; } catch (err) { return false; }
  };
  const decline = () => {
    declinedThisPage = true;
    try { sessionStorage.setItem(DECLINED_KEY, '1'); } catch (err) {}
  };

  // First match wins. Returns one cart request to make, or null when the cart is right.
  function planNext(cart, config, state) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const gifts = items.filter(isGift);
    const others = items.filter((i) => !isGift(i));
    const orphan = gifts.find((g) => !others.some(keepsGift(g)));
    if (orphan) return { type: 'change', key: orphan.key, quantity: 0 };
    if (gifts.length > 1) return { type: 'change', key: gifts[1].key, quantity: 0 };
    if (gifts.length === 1 && gifts[0].quantity > 1) return { type: 'change', key: gifts[0].key, quantity: 1 };
    const triggers = (i) => matches(i, config.triggerIds) && (!config.subscriptionsOnly || isSubscription(i));
    if (!gifts.length && config && !(state && state.declined) && others.some(triggers)) {
      const add = { type: 'add', variantId: config.giftVariantId, giftFor: config.triggerIds.join(',') };
      if (config.subscriptionsOnly) add.subscriptionsOnly = true;
      return add;
    }
    return null;
  }

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  async function request(path, body) {
    const opts = body
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
      : { headers: { Accept: 'application/json' } };
    const res = await fetch(root() + path, opts);
    if (!res.ok) throw new Error(`${path} HTTP ${res.status}`);
    const data = await res.json();
    if (!data || typeof data !== 'object') throw new Error(`${path} returned no object`);
    return data;
  }

  // The upsell popup can render the side cart from a cart read before the gift was added or removed (INT-515).
  const drawerStale = (cart) => {
    const inner = document.getElementById('CartDrawer');
    if (!cart || !inner || typeof inner.querySelector !== 'function') return false;
    return cart.items.some(isGift) !== !!inner.querySelector('.cart-item--ir-gift');
  };

  const seeCart = (cart) => {
    lastCart = cart;
    giftKnownInCart = cart.items.some(isGift);
  };

  // Re-render without opening the drawer (CartDrawer.renderContents always opens it).
  // `cart` is null when the final cart state is unknown; the theme then refreshes itself.
  async function refresh(cart) {
    const onCartPage = Array.from(document.querySelectorAll('cart-items')).some((el) => !el.closest('cart-drawer'));
    if (onCartPage) {
      location.reload();
      return;
    }
    try {
      if (!cart) throw new Error('cart state unknown after a failed request');
      // A side cart tap or save since this point wins; the side cart then redraws itself once it is quiet
      // (assets/cart-drawer.js, INT-525). Kit badges and the shipping bar still hear about the gift.
      const ticket = typeof cartDrawerTicket === 'function' ? cartDrawerTicket() : null;
      const sections = await request('?sections=cart-drawer,cart-icon-bubble');
      if (ticket !== null && !cartDrawerMayPaint(ticket)) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', irFreeGift: true, cartData: cart });
        return;
      }
      const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      const drawer = document.querySelector('cart-drawer');
      const drawerInner = document.getElementById('CartDrawer');
      const bubble = document.getElementById('cart-icon-bubble');
      const newInner = sections['cart-drawer'] && parse(sections['cart-drawer'], '#CartDrawer');
      if (drawerInner && !newInner) throw new Error('cart-drawer section missing');
      if (drawerInner) drawerInner.innerHTML = newInner.innerHTML;
      const newBubble = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble'], '.shopify-section');
      if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;
      if (drawer) {
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', () => drawer.close());
        drawer.classList.toggle('is-empty', cart.item_count === 0);
        // The focused element was replaced; keep focus inside an open drawer, as CartDrawer.open() does.
        if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
          const container = cart.item_count === 0 ? drawer.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
          trapFocus(container, drawer.querySelector('.drawer__inner') || drawer.querySelector('.drawer__close'));
        }
      }
      if (typeof cartDrawerChanged === 'function') cartDrawerChanged();
      if (typeof disableMaxedCartButtons === 'function') disableMaxedCartButtons();
      // source 'cart-items' makes CartItems skip a duplicate render; kit badges and the shipping bar still update.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', irFreeGift: true, cartData: cart });
    } catch (err) {
      console.error('[ir-free-gift]', err);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ir-free-gift', cartData: cart });
    }
  }

  async function run() {
    let cart = null;
    let changed = false;
    try {
      for (let mutations = 0; ; mutations++) {
        cart = await request('cart.js');
        seeCart(cart);
        const action = planNext(cart, config, { declined: isDeclined() });
        if (!action || mutations === MAX_MUTATIONS) break;
        if (action.type === 'add') {
          // A re-added gift usually gets the same line key as the one removed earlier.
          lastRemovedKey = null;
          const properties = { _ir_gift_for: action.giftFor };
          if (action.subscriptionsOnly) properties._ir_gift_sub = '1';
          await request('cart/add.js', { items: [{ id: action.variantId, quantity: 1, properties }] });
        } else {
          if (action.quantity === 0) lastRemovedKey = action.key;
          await request('cart/change.js', { id: action.key, quantity: action.quantity });
        }
        changed = true;
      }
    } catch (err) {
      console.error('[ir-free-gift]', err);
      // A failed request after an earlier change leaves the final cart unknown.
      if (changed) cart = null;
    }
    if (changed || drawerStale(cart)) await refresh(cart);
  }

  async function reconcile() {
    if (busy) {
      rerun = true;
      return;
    }
    busy = true;
    try {
      await run();
    } finally {
      busy = false;
    }
    if (rerun) {
      rerun = false;
      await reconcile();
    }
  }

  const eventHasGift = (data) => {
    const c = data && data.cartData;
    return !!c && typeof c === 'object' && (isGift(c) || (Array.isArray(c.items) && c.items.some(isGift)));
  };
  const eligible = (data) => !!config || giftKnownInCart || eventHasGift(data);

  // The shopper removed the gift from the drawer or /cart while its trigger is still there.
  const noteDecline = (cart) => {
    const previous = lastCart && lastCart.items.find(isGift);
    if (!previous || cart.items.some(isGift) || previous.key === lastRemovedKey) return;
    if (cart.items.some(keepsGift(previous))) decline();
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
      if (data && (data.irFreeGift || data.source === 'ir-free-gift')) return;
      const c = data && data.cartData;
      if (isFullCart(c)) {
        if (data.source === 'cart-items') noteDecline(c);
        seeCart(c);
      }
      if (eligible(data)) reconcile();
    });
  }

  const drawer = document.querySelector('cart-drawer');
  if (drawer && typeof MutationObserver === 'function') {
    let wasActive = drawer.classList.contains('active');
    new MutationObserver(() => {
      const active = drawer.classList.contains('active');
      if (active && !wasActive && eligible()) reconcile();
      wasActive = active;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  window.addEventListener('pageshow', () => {
    if (eligible()) reconcile();
  });

  // Checkout posts the cart forms' positional updates[] quantities, which can be stale after the gift line
  // is added or removed. Every quantity change is already saved by Ajax, so leave them out of the submit.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || (form.id !== 'CartDrawer-Form' && form.id !== 'cart')) return;
      if (!config && !giftKnownInCart) return;
      const inputs = Array.from(form.elements || []).filter((el) => el.name === 'updates[]');
      inputs.forEach((el) => { el.disabled = true; });
      // The form data is collected right after this event, so re-enabling on the next task is safe
      // and keeps the quantity boxes usable if the navigation is cancelled.
      setTimeout(() => inputs.forEach((el) => { el.disabled = false; }));
    },
    true
  );

  window.IRFreeGift = { planNext, reconcile };
})();
